import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./header.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import cx from 'classnames';
import { PopoverHorizontalOrientation, PopoverVerticalOrientation } from 'wds/popover';
import { storeAuthenticationContext } from '~/modules/analytics/authenticationContext';
import { AGENTEXCHANGE_HOST_NAME, APPX_JP_SITE_URL, IS_DEVELOPMENT, PUBLIC_URL, TBID_MOCK_USER } from '~/modules/config';
import { FeatureFlags } from '~/modules/feature_flags';
import { getUserLanguage } from '~/modules/helpers/languageHelper';
import { LEARN_BASE_ROUTE, Routes } from '~/modules/routes';
import { WireI18n, selectSharedLabels } from '~/modules/stores/i18nStore';
import { routerStore } from '~/modules/stores/routerStore';
import { selectIsAuthenticated, userStore } from '~/modules/stores/userStore';
import { DeviceType, selectDeviceType, viewportStore } from '~/modules/stores/viewportStore';
import { JAPANESE_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
import { parseJson } from '~/modules/utilities/object_utils';
import { isAgentExchange } from '~/modules/utilities/router';
const BASIC_HEADER_ROUTES = [Routes.TRIALS, Routes.TRIALS_CONFIRMATION];
const AGENTEXCHANGE_URL = `https://${AGENTEXCHANGE_HOST_NAME}`;
const AGENTEXCHANGE_JP_URL = `https://${AGENTEXCHANGE_HOST_NAME}/jp`;
class Header extends LightningElement {
  constructor(...args) {
    super(...args);
    this.isPhoneNavExpanded = false;
    this.displayTooltip = false;
    this.showBasicHeader = void 0;
    this.isLoggedIn = void 0;
    this.deviceType = void 0;
    this._hasRendered = false;
    this.handleMobileMenuKeyDownEvent = _event => {
      const event = _event;
      const navigationMenu = this.template.querySelector('.nav-mobile--open .header-nav-items');
      if (navigationMenu) {
        const activeElement = this.template.activeElement;
        const focusableItems = this.template.querySelectorAll('.close-button, .nav-mobile--open .header-nav-items a[href]');
        if (event.key === 'Tab') {
          /* shift + Tab */
          if (event.shiftKey) {
            // if I'm on the first item, then circle into the last item
            if (activeElement === focusableItems[0]) {
              focusableItems[focusableItems.length - 1].focus();
              event.preventDefault();
            }
            // if I'm on the second item then force going into the first of the list
            // this is because the default order would be Login, Signup then Close
            // Check if this is how we want it to behave
            if (activeElement === focusableItems[1]) {
              focusableItems[0].focus();
              event.preventDefault();
            }
          } /* tab */else {
            if (activeElement === focusableItems[focusableItems.length - 1]) {
              focusableItems[0].focus();
              event.preventDefault();
            }
          }
        }
      }
    };
    this.handleMobileMenuCloseEvent = event => {
      if (this.isPhoneNavExpanded && event.key === 'Escape') {
        this.toggleMenu();
      }
    };
  }
  setupLabels({
    header,
    shared
  }) {
    this.labels = header;
    this.sharedLabels = shared;
  }
  get isMobile() {
    return this.deviceType === DeviceType.PHONE;
  }
  renderedCallback() {
    if (this._hasRendered) {
      return;
    }
    this._hasRendered = true;
    const mobileMenu = this.template.querySelector('.header-nav-items');
    mobileMenu?.addEventListener('keydown', this.handleMobileMenuKeyDownEvent);
    window.addEventListener('keydown', this.handleMobileMenuCloseEvent);
  }
  disconnectedCallback() {
    const mobileMenu = this.template.querySelector('.nav-mobile--open .header-nav-items');
    mobileMenu?.removeEventListener('keydown', this.handleMobileMenuKeyDownEvent);
    window.removeEventListener('keydown', this.handleMobileMenuCloseEvent);
  }
  saveAuthenticationContext() {
    storeAuthenticationContext({
      origin: 'header'
    });
  }
  login() {
    this.saveAuthenticationContext();
    if (IS_DEVELOPMENT && TBID_MOCK_USER) {
      userStore.setMockedUser(parseJson(TBID_MOCK_USER));
      return;
    }
    window.SFIDWidget.login();
  }
  signup() {
    this.saveAuthenticationContext();
    window.SFIDWidget.signup();
  }
  get learnUrl() {
    if (FeatureFlags.FF_JAPANESE_SITE_LEARN_TAB) {
      return `/${LEARN_BASE_ROUTE}`;
    }
    return this.isUserLanguageJapanese ? '/mktcollections/curated/appexchange_contents' : '/learn';
  }
  get links() {
    if (this.isAgentExchange) {
      return {
        home: this.addPublicURL('/'),
        collections: this.addPublicURL('/collections'),
        consulting: this.addPublicURL('/consulting'),
        learn: this.addPublicURL(this.learnUrl),
        agentExchange: this.isUserLanguageJapanese ? AGENTEXCHANGE_JP_URL : AGENTEXCHANGE_URL
      };
    }
    return {
      home: '/',
      collections: '/collections',
      consulting: '/consulting',
      learn: this.learnUrl,
      agentExchange: this.isUserLanguageJapanese ? AGENTEXCHANGE_JP_URL : AGENTEXCHANGE_URL
    };
  }
  get bucketOptions() {
    const bucketLabels = this.labels.explore.buckets;
    const buckets = [{
      icon: '/images/icons.svg#business-needs',
      title: bucketLabels.businessNeeds.title,
      description: bucketLabels.businessNeeds.description,
      action: '/explore/business-needs'
    }, {
      icon: '/images/icons.svg#products',
      title: bucketLabels.products.title,
      description: bucketLabels.products.description,
      action: '/explore/products'
    }, {
      icon: '/images/icons.svg#industries',
      title: bucketLabels.industries.title,
      description: bucketLabels.industries.description,
      action: '/explore/industries'
    }, {
      icon: '/images/icons.svg#labs',
      title: bucketLabels.labs.title,
      description: bucketLabels.labs.description,
      action: '/explore/salesforce-labs'
    }, {
      icon: '/images/icons.svg#impact',
      title: bucketLabels.impact.title,
      description: bucketLabels.impact.description,
      action: '/explore/impact'
    }];
    if (FeatureFlags.FF_EXPLORE_AGENTFORCE_PAGE) {
      buckets.splice(1, 0, {
        icon: '/images/icons.svg#agentforce',
        title: bucketLabels.agentforce.title,
        description: bucketLabels.agentforce.description,
        action: '/explore/agentforce'
      });
    }
    if (this.isAgentExchange) {
      return buckets.map(bucket => ({
        ...bucket,
        action: this.addPublicURL(bucket.action)
      }));
    }
    return buckets;
  }
  handleClick() {
    this.displayTooltip = true;
  }
  handleKeyDown(event) {
    if (event.key === 'Enter') {
      this.handleClick();
    }
  }
  get popoverOrientation() {
    const popoverOrientation = {
      vertical: PopoverVerticalOrientation.BOTTOM,
      horizontal: PopoverHorizontalOrientation.RIGHT
    };
    return popoverOrientation;
  }
  get exploreMenuItemRef() {
    return this.template.querySelector('.explore-item a');
  }
  closePopover() {
    this.displayTooltip = false;
    this.exploreMenuItemRef?.focus();
  }
  handleTooltipKeyboardEvent(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closePopover();
    }
  }
  get outerClass() {
    const mobileClass = this.isPhoneNavExpanded && this.isMobile ? 'nav-mobile--open' : '';
    const basicClass = this.showBasicHeader ? 'basic' : '';
    return `nav-header ${mobileClass} ${basicClass}`;
  }
  toggleMenu() {
    this.isPhoneNavExpanded = !this.isPhoneNavExpanded;
  }
  get isUserLanguageJapanese() {
    return getUserLanguage() === JAPANESE_LANGUAGE_CODE;
  }
  handleAnalytics(event) {
    const {
      label,
      url
    } = event.detail.link.callToAction;
    const isExploreSubMenu = !!this.bucketOptions.find(item => item.title === label);
    event.detail.elementType = 'headerNavLink';
    event.detail.headerNavLink = {
      label,
      url
    };
    if (isExploreSubMenu) {
      event.detail.headerNavLink.level = 'l2';
      event.detail.headerNavLink.navItem = `Explore:${label}`;
    } else {
      event.detail.headerNavLink.level = 'l1';
      event.detail.headerNavLink.navItem = label;
    }
  }
  get agentExchangeCopyClasses() {
    return cx('agentexchange-copy', isAgentExchange() && 'agentexchange-copy-bright');
  }
  get isAgentExchange() {
    return isAgentExchange();
  }
  get isAppExchange() {
    return !isAgentExchange();
  }
  addPublicURL(relativeUrl) {
    const baseUrl = this.isUserLanguageJapanese ? APPX_JP_SITE_URL : PUBLIC_URL;
    return `${baseUrl}${relativeUrl}`;
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(Header, {
  wire: {
    showBasicHeader: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: routerStore,
          selector: selectShowBasicHeader
        };
      }
    },
    isLoggedIn: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: userStore,
          selector: selectIsAuthenticated
        };
      }
    },
    setupLabels: {
      adapter: WireI18n,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          selector: i18n => ({
            header: i18n.header,
            shared: selectSharedLabels(i18n)
          })
        };
      }
    },
    deviceType: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: viewportStore,
          selector: selectDeviceType
        };
      }
    }
  },
  fields: ["isPhoneNavExpanded", "displayTooltip", "_hasRendered", "handleMobileMenuKeyDownEvent", "handleMobileMenuCloseEvent"]
});
const __lwc_component_class_internal = _registerComponent(Header, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;
export function selectShowBasicHeader(state = routerStore.getState()) {
  const result = BASIC_HEADER_ROUTES.includes(state.url);
  return result;
}